// ═══════════════════════════════════════════════════════════════════════════════════════════════════
// provisioningHealth.ts — typed API client for the agent provisioning health diagnostic
// ═══════════════════════════════════════════════════════════════════════════════════════════════════
//
// Backs the "Provisioning Health" drawer on AgentDetailPage. One endpoint:
//
//   GET /api/AgentProvisioning/Health/{agentId}
//
// Returns a per-area pass/warn/fail report covering DB row, Foundry
// registration, skills, KB linkage, peer wirings, test prompts, scenarios.
// Pure read — safe to call repeatedly while the operator iterates on
// onboarding (e.g. wiring peers, then re-checking).
//
// NOTE: this endpoint returns the report DTO directly (no OperationResult
// wrapper) — different from sibling endpoints like AgentReasoning that
// wrap in OperationResult<T>. The controller chose direct return because
// the manager handles all per-area failures internally and never throws.
// ═══════════════════════════════════════════════════════════════════════════════════════════════════

import { apiGet } from './client';

// ────────────────────────────────────────────────────────────────────────────
// Types — match the C# records in IAgentProvisioningHealthManager.cs
// ────────────────────────────────────────────────────────────────────────────

/** Per-area severity. Wire-format is the C# enum name (PascalCase). */
export type HealthStatus = 'Healthy' | 'Warning' | 'Failing';

/** Roll-up status for the whole report. Wire-format is the C# enum name. */
export type HealthOverall = 'ReadyForProduction' | 'Issues' | 'NotReady';

/** Stable per-area key. Used as React row key + analytics dimension. */
export type HealthArea =
  | 'AgentRow'
  | 'FoundryAgent'
  | 'Skills'
  | 'KnowledgeBases'
  | 'PatientBriefing'
  | 'PeerWirings'
  | 'TestPrompts'
  | 'Scenarios';

/**
 * One per-area check. Drives a single card in the drawer.
 * Detail can contain newlines; render as a code-style block.
 * Remediation is empty when status is Healthy.
 */
export interface HealthCheck {
  area: HealthArea;
  displayName: string;
  status: HealthStatus;
  summary: string;
  detail: string;
  remediation: string;
}

/** Full report returned by GET /api/AgentProvisioning/Health/{agentId}. */
export interface AgentProvisioningHealthReport {
  agentId: number;
  agentDisplayName: string | null;
  canonicalName: string | null;
  /** ISO-8601 UTC timestamp from the server. */
  checkedAtUtc: string;
  overall: HealthOverall;
  checks: HealthCheck[];
}

// ────────────────────────────────────────────────────────────────────────────
// API call
// ────────────────────────────────────────────────────────────────────────────

/**
 * Run the read-only provisioning health check for an agent. The drawer
 * calls this on open and on each "Recheck" button click. Pure reads, so
 * safe to call frequently.
 */
export async function getAgentProvisioningHealth(
  agentId: number,
): Promise<AgentProvisioningHealthReport> {
  return apiGet<AgentProvisioningHealthReport>(
    `/api/AgentProvisioning/Health/${agentId}`,
  );
}
